import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Backdrop, Box, CircularProgress, IconButton, Typography, Chip, Button,
    Accordion, AccordionSummary, AccordionDetails, ToggleButtonGroup, ToggleButton,
    TextField, Autocomplete, Select, MenuItem, FormControl, InputLabel, InputAdornment,
    Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
    WorkOutline as WorkOutlineIcon, Place as PlaceIcon,
    Visibility as VisibilityIcon, Download as DownloadIcon,
    Mic as MicIcon, MicOff as MicOffIcon,
    QrCodeScanner as QrCodeScannerIcon,
    Link as LinkIcon, AddLink as AddLinkIcon,
    AutoAwesome as AutoAwesomeIcon, History as HistoryIcon,
    Shield as ShieldIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, ALT_JOB_STORE, TOOL_DOCS_STORE, TOOL_CALIBRATION_STORE, SRD_INSPECTION_STORE } from '../../../config/constants';
import SignatureModal from '../../jha/SignatureModal';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import QrScannerModal from '../../shared/QrScannerModal';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import axiosInstance from '../../../axiosInstance';
import useDictation from '../../../hooks/useDictation';
import useSftpDownload from '../gas-monitor/useSftpDownload';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';
import srdInspectionService from '../../../services/srdInspectionService';
import SrdDeviceDiagram from './SrdDeviceDiagram';
import {
    STATUS_DRAFT, STATUS_SUBMITTED, LIFELINE_MATERIALS,
    SRD_TOOL_FILTERS, INSPECTION_SECTIONS, INSPECTION_GROUPS, ALL_CHECK_KEYS
} from './constants';

const SrdInspectionForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const MODE_JOB = 'job';
    const MODE_LOCATION = 'location';

    const [jobs, setJobs] = useState([]);
    const [altJobs, setAltJobs] = useState([]);
    const [tools, setTools] = useState([]);
    const [toolsLoading, setToolsLoading] = useState(true);
    const [toolsError, setToolsError] = useState('');
    const [jobMode, setJobMode] = useState(MODE_JOB);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        status: STATUS_DRAFT,
        // Tool info
        toolId: null, toolPart: '', toolSerialNo: '', toolModel: '', toolManufacturer: '', toolDescription: '',
        lotNo: '', dateOfManufacture: '', length: '', lifelineMaterial: '',
        ownerCompany: '', inServiceDate: '',
        // Job
        jobCode: '', customerName: '', jobLocation: '',
        // Inspection
        inspectionDate: dayjs().format('YYYY-MM-DD'),
        // === V2 (вайрфрейм 05.05.2026): 4 секции, 10 пунктов ===
        // Connectors (2)
        connectorsLatch: '', connectorsCorrosion: '',
        // Housing (3)
        housingLabels: '', housingMarkings: '', housingCorrosion: '',
        // Shock Pack (2)
        shockPackCover: '', shockPackDamage: '',
        // Verify Lifeline Operation (3)
        lifelineLockTest: '', lifelineWebInspection: '', lifelineRetraction: '',
        // Overall
        overallResult: '',
        // Notes
        notes: '',
        // Inspector
        inspectorName: userFullName, employeeId: userId,
        signature: '', signatureDate: dayjs().format('YYYY-MM-DD'),
        // Meta
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const [toolHistory, setToolHistory] = useState({ inspections: [], calibrations: [], lastCalDate: null, nextCalDate: null });
    const [toolHistoryLoading, setToolHistoryLoading] = useState(false);
    // QR-привязка: токен который сканировали но он ещё не привязан к девайсу.
    // Когда пользователь выберет devise — появится кнопка «Link this QR to selected device».
    const [pendingQrToken, setPendingQrToken] = useState('');
    const [qrActionLoading, setQrActionLoading] = useState(false);
    // Режим работы сканера:
    //   'select' — открыт через главную кнопку Scan возле селекта.
    //              Сканишь QR другого SRD device → переключаем выбор без warning.
    //   'link'   — открыт через «Scan to Link» (device выбран без QR).
    //              Сканишь QR уже привязанный к другому device → warning, не переключаем.
    const [qrScanMode, setQrScanMode] = useState('select');
    // Диаграмма открыта по умолчанию (юзер: «всегда открыт»). Lazy-render
    // оставлен — если юзер свернёт и развернёт обратно, SVG монтируется заново.
    const [diagramOpen, setDiagramOpen] = useState(true);
    // View Last Inspection: загружаем PDF в существующий PdfPreviewModal
    const [lastInspLoading, setLastInspLoading] = useState(false);

    const saveTimer = useRef(null);
    const { docLoading, handleDownload } = useSftpDownload();
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);

    const handleViewPdf = async (sftpPath, docId) => {
        setPdfLoading(true);
        setPdfPreviewOpen(true);
        setPdfFilename(sftpPath.split(/[/\\]/).pop() || 'certificate.pdf');
        const blob = await handleDownload(sftpPath, docId, 'blob');
        if (blob) setPdfBlob(blob);
        setPdfLoading(false);
    };

    // Voice dictation
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation();

    const toggleDictation = () => {
        if (isListening) {
            stopDictation();
        } else {
            startDictation({
                onResult: (transcript) => {
                    const current = form.notes || '';
                    const needsSpace = current && !current.endsWith(' ');
                    const newText = current + (needsSpace ? ' ' : '') + transcript + ' ';
                    handleFieldChange('notes', newText);
                },
            });
        }
    };

    const loadToolHistory = async (part) => {
        if (!part) { setToolHistory({ inspections: [], calibrations: [], lastCalDate: null, nextCalDate: null }); return; }
        setToolHistoryLoading(true);
        try {
            // Try API first
            const { data } = await axiosInstance.get(`/srd-inspection/tool-status?part=${encodeURIComponent(part)}`);
            if (data.success) {
                setToolHistory({
                    inspections: data.inspections || [],
                    calibrations: data.calibrations || [],
                    lastCalDate: data.lastCalDate,
                    nextCalDate: data.nextCalDate,
                });
                setToolHistoryLoading(false);
                return;
            }
        } catch (e) { /* offline — fallback to IndexedDB */ }

        // Fallback: local data
        try {
            const db = await initializeDB();
            const calibrations = [];
            if (db.objectStoreNames.contains(TOOL_CALIBRATION_STORE)) {
                const all = await db.transaction(TOOL_CALIBRATION_STORE, 'readonly').objectStore(TOOL_CALIBRATION_STORE).getAll();
                const forPart = (all || []).filter(c => c.part === part).sort((a, b) => (b.calDate || '').localeCompare(a.calDate || '')).slice(0, 3);
                forPart.forEach(c => calibrations.push({ calDate: c.calDate, calWho: c.calWho, calNextDate: c.calNextDate }));
            }
            const inspections = [];
            if (db.objectStoreNames.contains(SRD_INSPECTION_STORE)) {
                const all = await db.transaction(SRD_INSPECTION_STORE, 'readonly').objectStore(SRD_INSPECTION_STORE).getAll();
                const forPart = (all || []).filter(i => i.toolPart === part && i.status === 'submitted')
                    .sort((a, b) => (b.inspectionDate || '').localeCompare(a.inspectionDate || '')).slice(0, 3);
                forPart.forEach(i => inspections.push({
                    inspectionDate: i.inspectionDate, overallResult: i.overallResult,
                    inspectorName: i.inspectorName, submittedBy: i.submittedByName,
                }));
            }
            setToolHistory({
                inspections, calibrations,
                lastCalDate: calibrations[0]?.calDate || null,
                nextCalDate: calibrations[0]?.calNextDate || null,
            });
        } catch (e) { console.error('Failed to load local tool history:', e); }
        finally { setToolHistoryLoading(false); }
    };

    const sortedJobs = useMemo(() =>
        jobs.map(j => ({ ...j, label: `${j.Job} - ${j.JobName}` }))
            .sort((a, b) => (parseInt(b.Job) || 0) - (parseInt(a.Job) || 0)),
    [jobs]);

    const locationOptions = useMemo(() => {
        const jobLocations = jobs
            .filter(j => j.Location || j.location)
            .map(j => ({ label: j.Location || j.location, isAltJob: false }));
        const altLocations = altJobs.map(a => ({ label: a.name || a.Name || '', isAltJob: true })).filter(a => a.label);
        const seen = new Set();
        return [...jobLocations, ...altLocations].filter(l => {
            if (seen.has(l.label)) return false;
            seen.add(l.label);
            return true;
        }).sort((a, b) => a.label.localeCompare(b.label));
    }, [jobs, altJobs]);

    const filteredTools = useMemo(() =>
        tools
            .filter(t => SRD_TOOL_FILTERS.includes((t.type || '').trim().toLowerCase()))
            .map(t => ({ ...t, label: `${t.part || ''} — ${t.serialNo || ''} — ${t.model || ''} (${t.manufacturer || ''})` })),
    [tools]);

    // Auto-compute overall result.
    //   ANY fail (даже если не все пункты заполнены) → 'fail'.
    //   Все заполнены и без fail → 'pass'.
    //   Иначе (есть незаполненные, нет fail) → '' (юзер ещё не закончил).
    useEffect(() => {
        const values = ALL_CHECK_KEYS.map(k => form[k]);
        const filled = values.filter(v => v);
        const hasFail = filled.some(v => v === 'fail');
        const allFilled = filled.length === ALL_CHECK_KEYS.length;
        let newOverall;
        if (hasFail) newOverall = 'fail';
        else if (allFilled) newOverall = 'pass';
        else newOverall = '';
        if (form.overallResult !== newOverall) {
            const u = { ...form, overallResult: newOverall };
            setForm(u); queueSave(u);
        }
    }, ALL_CHECK_KEYS.map(k => form[k]));

    // Load jobs/draft из IndexedDB (как и раньше)
    useEffect(() => {
        (async () => {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(JOB_STORE)) setJobs(await db.transaction(JOB_STORE, 'readonly').objectStore(JOB_STORE).getAll() || []);
            if (db.objectStoreNames.contains(ALT_JOB_STORE)) setAltJobs(await db.transaction(ALT_JOB_STORE, 'readonly').objectStore(ALT_JOB_STORE).getAll() || []);
            if (uuid) {
                const existing = await srdInspectionService.loadByUuid(uuid);
                if (existing) {
                    setForm(existing);
                    setSubmitted(existing.status === STATUS_SUBMITTED);
                    if (existing.jobMode) setJobMode(existing.jobMode);
                    if (existing.toolPart) loadToolHistory(existing.toolPart);
                }
            }
        })();
    }, [uuid]);

    // Load SRD devices напрямую из внешней БД (через Symfony API), не из IndexedDB.
    useEffect(() => {
        (async () => {
            setToolsLoading(true);
            setToolsError('');
            try {
                const { data } = await axiosInstance.get('/srd-inspection/tools');
                if (data?.success) {
                    setTools(data.tools || []);
                } else {
                    setToolsError(data?.message || 'Failed to load SRD devices');
                }
            } catch (e) {
                setToolsError(e.response?.data?.message || e.message || 'Failed to load SRD devices');
            } finally {
                setToolsLoading(false);
            }
        })();
    }, []);

    const queueSave = (d) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { srdInspectionService.saveDraft(d).catch(console.error); }, 1000);
    };

    const handleFieldChange = (field, value) => { const u = { ...form, [field]: value }; setForm(u); queueSave(u); };

    const handleModeChange = (_, newMode) => {
        if (!newMode || isReadOnly) return;
        setJobMode(newMode);
        const u = { ...form, jobMode: newMode, jobCode: '', customerName: '', jobLocation: '' };
        setForm(u); queueSave(u);
    };

    const handleJobChange = (_, v) => {
        const u = v ? { ...form, jobCode: v.Job || '', customerName: v.Customer || '', jobLocation: v.Location || v.location || '' }
                    : { ...form, jobCode: '', customerName: '', jobLocation: '' };
        setForm(u); queueSave(u);
    };

    const handleLocationChange = (_, v) => {
        const u = v ? { ...form, jobLocation: v.label || '', jobCode: '', customerName: '' }
                    : { ...form, jobLocation: '', jobCode: '', customerName: '' };
        setForm(u); queueSave(u);
    };

    const handleToolChange = (_, v) => {
        const u = v ? {
            ...form,
            toolId: v.externalId ?? v.id ?? null,
            toolPart: v.part || '', toolSerialNo: v.serialNo || '', toolModel: v.model || '',
            toolManufacturer: v.manufacturer || '', toolDescription: v.description || '',
        } : {
            ...form,
            toolId: null,
            toolPart: '', toolSerialNo: '', toolModel: '', toolManufacturer: '', toolDescription: '',
        };
        setForm(u); queueSave(u);
        loadToolHistory(u.toolPart);
    };

    // QR scan flow — два режима (qrScanMode):
    //   'select' (общая кнопка Scan): сканированный QR linked к SRD device →
    //     переключаем выбор. blank/unknown → pendingQrToken flow.
    //   'link' («Scan to Link» когда выбран device без QR): сканированный QR linked
    //     к другому device → warning «уже назначен, возьмите другой». blank/unknown
    //     → линкуем к выбранному device.
    const handleQrDetected = async (token) => {
        const mode = qrScanMode; // зафиксируем — модал сейчас закрыт
        setQrScannerOpen(false);
        if (!token) return;
        const lcToken = token.toLowerCase();

        const showAlreadyAssigned = (linkedPart, linkedSn) => {
            Swal.fire({
                icon: 'warning',
                title: 'QR is already assigned',
                html: `This QR is already assigned to <b>${linkedPart}${linkedSn ? ' / ' + linkedSn : ''}</b>.<br>Use a different QR code.`,
            });
        };

        // 1) Найден среди уже загруженных devices?
        const tool = tools.find(t => (t.qrToken || '').toLowerCase() === lcToken);
        if (tool) {
            const isSrd = SRD_TOOL_FILTERS.includes((tool.type || '').trim().toLowerCase());
            if (!isSrd) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Not an SRD device',
                    text: `This tool is «${tool.type || 'unknown'}» — the SRD inspection is for Retractable Lifelines only.`,
                });
                return;
            }
            // В режиме link — этот QR УЖЕ привязан, нельзя переиспользовать.
            if (mode === 'link') {
                const sameDevice = form.toolPart === (tool.part || '')
                    && (form.toolSerialNo || '') === (tool.serialNo || '');
                if (!sameDevice) {
                    showAlreadyAssigned(tool.part, tool.serialNo);
                    return;
                }
                // Уже linked к этому же device — молча.
                return;
            }
            // mode === 'select' — выбираем (или переключаемся).
            handleToolChange(null, tool);
            return;
        }

        // 2) Не найден локально — спрашиваем сервер
        let scanData = null;
        try {
            const resp = await axiosInstance.get(`/external-tool-qr/scan/${lcToken}`);
            scanData = resp.data;
        } catch (e) { /* offline — попробуем как fresh link */ }

        // 2a) Привязан к device. tools[] мог быть загружен ДО привязки, поэтому
        // qrToken мог не подцепиться — ищем по part+serialNo.
        if (scanData?.linked && scanData.qr) {
            const linkedTool = tools.find(t =>
                (t.part || '') === (scanData.qr.part || '') &&
                (t.serialNo || '') === (scanData.qr.serialNo || '')
            );
            if (linkedTool) {
                // Обновим qrToken в state (повторный скан попадёт сразу в case 1).
                setTools(prev => prev.map(t => (
                    t.part === linkedTool.part && t.serialNo === linkedTool.serialNo
                        ? { ...t, qrToken: lcToken } : t
                )));
                if (mode === 'link') {
                    const sameDevice = form.toolPart === (linkedTool.part || '')
                        && (form.toolSerialNo || '') === (linkedTool.serialNo || '');
                    if (!sameDevice) {
                        showAlreadyAssigned(linkedTool.part, linkedTool.serialNo);
                        return;
                    }
                    return; // уже linked к выбранному device
                }
                handleToolChange(null, linkedTool);
                return;
            }
            Swal.fire({
                icon: 'warning',
                title: 'Linked to a different device',
                text: `This QR is linked to ${scanData.qr.part}${scanData.qr.serialNo ? ' / ' + scanData.qr.serialNo : ''}, which is not in the SRD list.`,
            });
            return;
        }

        // 2a.5) Токен ВООБЩЕ не существует в системе — линковать нельзя.
        // Юзер должен сначала сгенерить blank в админке.
        if (scanData && scanData.exists === false) {
            Swal.fire({
                icon: 'error',
                title: 'QR not found',
                text: 'This QR code is not in the system. Generate a blank QR in the admin (SRD QR Labels) first, print it, then scan again.',
            });
            return;
        }

        // 2b) Токен — blank в БД. Если у юзера УЖЕ выбран девайс — линкуем
        // сразу без лишних подтверждений.
        if (form.toolPart) {
            Swal.fire({
                title: 'Linking…',
                html: `Assigning QR <code>${lcToken.slice(0,8)}…</code> to <b>${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}</b>`,
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });
            setQrActionLoading(true);
            try {
                const { data } = await axiosInstance.post('/external-tool-qr/link', {
                    part: form.toolPart,
                    serialNo: form.toolSerialNo || null,
                    token: lcToken,
                    linkedBy: userFullName || null,
                });
                if (!data?.success) throw new Error(data?.message || 'Link failed');
                refreshSelectedToolQr(form.toolPart, form.toolSerialNo, lcToken);
                Swal.fire({
                    icon: 'success', title: 'Linked!',
                    text: `QR ${lcToken.slice(0, 8)}… → ${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}`,
                    timer: 1800, showConfirmButton: false,
                });
            } catch (e) {
                Swal.fire({ icon: 'error', title: 'Link failed', text: e.response?.data?.message || e.message });
            } finally { setQrActionLoading(false); }
            return;
        }

        // 3) Девайс не выбран — pendingQrToken flow (юзер выберет devise в селекте).
        setPendingQrToken(lcToken);
        Swal.fire({
            icon: 'info',
            title: 'QR not linked yet',
            html: `Pick an SRD device from the list below, then press <b>Link this QR</b> to assign code <code>${lcToken.slice(0, 8)}…</code> to it.`,
            confirmButtonText: 'OK',
        });
    };

    const refreshSelectedToolQr = (part, serialNo, qrToken) => {
        // Локально обновляем массив tools чтобы UI сразу отразил привязку,
        // без перезагрузки всего списка с сервера.
        setTools(prev => prev.map(t => (
            t.part === part && t.serialNo === serialNo ? { ...t, qrToken } : t
        )));
    };

    const handleLinkPendingQr = async () => {
        if (!pendingQrToken || !form.toolPart) return;
        // Сразу показываем «в процессе» — Swal с loader, чтобы юзер видел что идёт работа.
        Swal.fire({
            title: 'Linking…',
            html: `Assigning QR <code>${pendingQrToken.slice(0,8)}…</code> to <b>${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}</b>`,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });
        setQrActionLoading(true);
        try {
            const { data } = await axiosInstance.post('/external-tool-qr/link', {
                part: form.toolPart,
                serialNo: form.toolSerialNo || null,
                token: pendingQrToken,
                linkedBy: userFullName || null,
            });
            if (!data?.success) throw new Error(data?.message || 'Link failed');
            refreshSelectedToolQr(form.toolPart, form.toolSerialNo, pendingQrToken);
            setPendingQrToken('');
            Swal.fire({
                icon: 'success', title: 'Linked!',
                text: `QR ${pendingQrToken.slice(0,8)}… → ${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}`,
                timer: 1800, showConfirmButton: false,
            });
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Link failed', text: e.response?.data?.message || e.message });
        } finally { setQrActionLoading(false); }
    };

    /**
     * View last submitted SRD inspection PDF for the selected device.
     * Открывается в существующем PdfPreviewModal — никаких новых модалок.
     */
    const handleViewLastInspection = async () => {
        if (!form.toolPart || lastInspLoading) return;
        setLastInspLoading(true);
        try {
            const params = new URLSearchParams({ part: form.toolPart });
            if (form.toolSerialNo) params.append('serialNo', form.toolSerialNo);
            const response = await axiosInstance.get(`/srd-inspection/last-pdf?${params}`, {
                responseType: 'blob',
                validateStatus: (s) => s < 500, // ловим 404 как обычный ответ
            });
            if (response.status === 404) {
                Swal.fire({
                    icon: 'info',
                    title: 'No previous inspection',
                    text: `No submitted SRD inspection found for ${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}.`,
                });
                return;
            }
            if (response.status !== 200) throw new Error('Failed to load last inspection');

            setPdfFilename(`SrdInspection_${form.toolPart}_last.pdf`);
            setPdfBlob(response.data);
            setPdfPreviewOpen(true);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'Failed to load PDF' });
        } finally { setLastInspLoading(false); }
    };

    const selectedToolClass = useMemo(() => {
        const t = tools.find(x => x.part === form.toolPart && x.serialNo === form.toolSerialNo);
        return t?.toolClass || '';
    }, [tools, form.toolPart, form.toolSerialNo]);

    const handleSignatureSave = (sig) => {
        const u = { ...form, signature: sig || '', signatureDate: sig ? dayjs().format('YYYY-MM-DD') : '' };
        setForm(u); queueSave(u); setSignatureModalOpen(false);
    };

    const handleSelectEmployee = (p) => {
        const u = { ...form, employeeId: p.id || '', inspectorName: p.FullName || p.name || '' };
        setForm(u); queueSave(u); setEmployeeModalOpen(false);
    };

    const handleSubmit = async () => {
        const newErrors = {};
        if (!form.toolPart) newErrors.toolPart = 'Tool is required';
        if (!form.inspectionDate) newErrors.inspectionDate = 'Inspection date is required';
        if (!form.signature) newErrors.signature = 'Signature is required';
        if (!form.inspectorName) newErrors.inspectorName = 'Inspector name is required';

        const filledChecks = ALL_CHECK_KEYS.filter(k => form[k]);
        if (filledChecks.length === 0) newErrors.checks = 'At least one inspection item must be checked';

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please fill in all required fields' });
            return;
        }

        const result = await Swal.fire({ title: 'Submit Inspection?', text: 'Once submitted, the form cannot be edited', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, submit it' });
        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            const submitData = { ...form, status: STATUS_SUBMITTED };
            const response = await srdInspectionService.submit(submitData);
            await srdInspectionService.delete(form.uuid);
            if (response?.toolDoc) {
                try {
                    const db = await initializeDB();
                    if (db.objectStoreNames.contains(TOOL_DOCS_STORE)) {
                        await db.transaction(TOOL_DOCS_STORE, 'readwrite').objectStore(TOOL_DOCS_STORE).put(response.toolDoc);
                    }
                } catch (e) { console.error('Failed to save toolDoc to IndexedDB:', e); }
            }
            Swal.fire({ icon: 'success', title: 'Success!', text: 'SRD Inspection submitted' });
            navigate('/operations/srd-inspection');
        } catch (e) {
            console.error('Submit error:', e);
            Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to submit inspection' });
        } finally { setSaving(false); }
    };

    const isReadOnly = submitted;

    const renderCheckSection = (sectionKey) => {
        const section = INSPECTION_SECTIONS[sectionKey];
        return (
            <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }} key={sectionKey}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' }, backgroundColor: section.color + '10' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: section.color }}>
                        {section.title}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
                    {section.items.map(item => (
                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>{item.label}</Typography>
                            <ToggleButtonGroup
                                value={form[item.key]} exclusive size="small" disabled={isReadOnly}
                                onChange={(_, v) => { if (v) handleFieldChange(item.key, v); }}
                                sx={{ ml: 1 }}
                            >
                                <ToggleButton value="pass" sx={{
                                    px: 1.5, py: 0.25, fontSize: '0.75rem',
                                    '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', '&:hover': { backgroundColor: '#43a047' } }
                                }}>Pass</ToggleButton>
                                <ToggleButton value="fail" sx={{
                                    px: 1.5, py: 0.25, fontSize: '0.75rem',
                                    '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', '&:hover': { backgroundColor: '#e53935' } }
                                }}>Fail</ToggleButton>
                                <ToggleButton value="na" sx={{
                                    px: 1.5, py: 0.25, fontSize: '0.75rem',
                                    '&.Mui-selected': { backgroundColor: '#9e9e9e', color: '#fff', '&:hover': { backgroundColor: '#757575' } }
                                }}>N/A</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/operations/srd-inspection')} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>SRD INSPECTION</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={submitted ? 'Submitted' : 'Draft'} size="small" sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />
                        {form.overallResult && (
                            <Chip
                                icon={form.overallResult === 'pass' ? <CheckCircleIcon /> : <CancelIcon />}
                                label={form.overallResult === 'pass' ? 'PASS' : 'FAIL'}
                                size="small"
                                sx={{ backgroundColor: form.overallResult === 'pass' ? '#4caf50' : '#f44336', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>

                    {/* Tool Selection */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Tool Selection</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                                <Autocomplete
                                    fullWidth
                                    options={filteredTools}
                                    getOptionLabel={o => o.label || ''}
                                    value={filteredTools.find(t => t.part === form.toolPart && t.serialNo === form.toolSerialNo) || null}
                                    onChange={handleToolChange}
                                    disabled={isReadOnly || toolsLoading}
                                    loading={toolsLoading}
                                    noOptionsText={toolsError ? `Error: ${toolsError}` : 'No SRD devices found'}
                                    renderInput={p => (
                                        <TextField
                                            {...p}
                                            label={toolsLoading ? 'Loading SRD devices…' : 'Select SRD Device *'}
                                            size="small"
                                            error={!!errors.toolPart || !!toolsError}
                                            helperText={errors.toolPart || toolsError}
                                            InputProps={{
                                                ...p.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {toolsLoading && <CircularProgress size={16} />}
                                                        {p.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={() => { setQrScanMode('select'); setQrScannerOpen(true); }}
                                    disabled={isReadOnly || toolsLoading}
                                    size="small"
                                    startIcon={<QrCodeScannerIcon />}
                                    sx={{ flexShrink: 0, height: 40, mt: 0 }}
                                >
                                    Scan
                                </Button>
                            </Box>

                            {/* Pending QR banner: scanned a fresh code, waiting for user to pick a device */}
                            {pendingQrToken && (
                                <Box sx={{
                                    backgroundColor: '#fff8e1', border: '1px solid #ffb300', borderRadius: 1,
                                    p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                                }}>
                                    <AddLinkIcon sx={{ color: '#f57c00', fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                                        Scanned QR <code>{pendingQrToken.slice(0, 8)}…</code> is not linked yet.
                                        {form.toolPart
                                            ? ` Click «Link this QR» to assign it to ${form.toolPart}${form.toolSerialNo ? ' / ' + form.toolSerialNo : ''}.`
                                            : ' Pick a device from the list above first.'}
                                    </Typography>
                                    <Button
                                        size="small" variant="contained" color="warning"
                                        startIcon={<LinkIcon />}
                                        disabled={!form.toolPart || qrActionLoading || isReadOnly}
                                        onClick={handleLinkPendingQr}
                                    >
                                        {qrActionLoading ? <CircularProgress size={14} /> : 'Link this QR'}
                                    </Button>
                                    <Button size="small" onClick={() => setPendingQrToken('')} disabled={qrActionLoading}>
                                        Cancel
                                    </Button>
                                </Box>
                            )}

                            {form.toolPart && (
                                <>
                                    {(() => {
                                        const t = tools.find(x => x.part === form.toolPart && x.serialNo === form.toolSerialNo) || {};
                                        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US') : '—';
                                        const fmtBool = (b) => b === true ? 'Yes' : b === false ? 'No' : '—';
                                        return (
                                            <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 1, fontSize: '0.8rem', lineHeight: 1.6, mb: 1 }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    <strong>Tool #:</strong> {form.toolPart} &nbsp;|&nbsp;
                                                    <strong>S/N:</strong> {form.toolSerialNo || '—'} &nbsp;|&nbsp;
                                                    <strong>Model:</strong> {form.toolModel || '—'} &nbsp;|&nbsp;
                                                    <strong>Mfr:</strong> {form.toolManufacturer || '—'} &nbsp;|&nbsp;
                                                    <strong>Class:</strong> {t.toolClass || selectedToolClass || '—'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    <strong>Category:</strong> {t.category || '—'} &nbsp;|&nbsp;
                                                    <strong>Division:</strong> {t.division || '—'} &nbsp;|&nbsp;
                                                    <strong>Location:</strong> {t.location || '—'} &nbsp;|&nbsp;
                                                    <strong>Inv. Status:</strong> {t.inventoryStatus || '—'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    <strong>Last Inspect:</strong> {fmtDate(t.dateInspect)} &nbsp;|&nbsp;
                                                    <strong>Insp Interval:</strong> {t.inspInterval ? `${t.inspInterval} mo` : '—'} &nbsp;|&nbsp;
                                                    <strong>Cal Required:</strong> {fmtBool(t.isCal)} {t.isCal && t.calInterval ? ` (${t.calInterval} mo)` : ''} &nbsp;|&nbsp;
                                                    <strong>3rd Party:</strong> {t.thirdParty || '—'}
                                                </Typography>
                                                {(t.whoAssigned || t.locationAssigned) && (
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                        <strong>Assigned to:</strong> {t.whoAssigned || '—'} &nbsp;|&nbsp;
                                                        <strong>At:</strong> {t.locationAssigned || '—'}
                                                    </Typography>
                                                )}
                                                {form.toolDescription && (
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                        {form.toolDescription}
                                                    </Typography>
                                                )}
                                                {/* View Last Inspection: открывает PDF в существующем PdfPreviewModal */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                    <Button
                                                        size="small" variant="outlined"
                                                        startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                                                        onClick={handleViewLastInspection}
                                                        disabled={lastInspLoading}
                                                        sx={{ py: 0, fontSize: '0.75rem' }}
                                                    >
                                                        {lastInspLoading
                                                            ? <CircularProgress size={14} />
                                                            : 'View last inspection'}
                                                    </Button>
                                                </Box>
                                                {/* QR-привязка. Генерация и печать — только в админке.
                                                    Линковка существующего стикера через сканер — здесь. */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                        <strong>QR Code:</strong>
                                                    </Typography>
                                                    {t.qrToken ? (
                                                        <Chip
                                                            size="small"
                                                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                                            label={`Linked · ${t.qrToken.slice(0, 8)}…`}
                                                            sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, height: 22 }}
                                                        />
                                                    ) : (
                                                        <>
                                                            <Chip size="small" label="Not linked"
                                                                sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600, height: 22 }} />
                                                            {!isReadOnly && (
                                                                <Button
                                                                    size="small" variant="outlined" color="primary"
                                                                    startIcon={<QrCodeScannerIcon sx={{ fontSize: 16 }} />}
                                                                    onClick={() => { setQrScanMode('link'); setQrScannerOpen(true); }}
                                                                    sx={{ py: 0, fontSize: '0.75rem' }}
                                                                >
                                                                    Scan to Link
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })()}

                                    {/* Tool History (Last/Next Cal + 3 last calibrations) — скрыт по
                                        просьбе клиента 05.05.2026. Кнопка «View last inspection» выше
                                        делает то же самое (приоритет Symfony → External). */}
                                </>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* SRL Anatomy diagram — открыт по умолчанию.
                        Visual reference for inspectors before they go through the checklist. */}
                    <Accordion
                        defaultExpanded
                        sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}
                        onChange={(_, expanded) => setDiagramOpen(expanded)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' },
                                background: 'linear-gradient(90deg, #e3f2fd 0%, #ffffff 100%)',
                            }}>
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#1565c0', mr: 0.75 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1565c0' }}>
                                Device Diagram (SRL Anatomy)
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
                                — visual reference, hover parts to see what to check
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {/* Render SVG only after first expand to keep the form snappy */}
                            <AnimatePresence>
                                {diagramOpen && (
                                    <motion.div
                                        key="srl-diagram"
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.35, ease: 'easeOut' }}
                                    >
                                        <SrdDeviceDiagram />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </AccordionDetails>
                    </Accordion>

                    {/* Device Details — hidden for now
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Device Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 0.75 }}>
                                <TextField label="Lot #" value={form.lotNo} size="small" disabled={isReadOnly}
                                    onChange={e => handleFieldChange('lotNo', e.target.value)} />
                                <TextField label="Mfr Date" type="date" value={form.dateOfManufacture} size="small"
                                    disabled={isReadOnly} onChange={e => handleFieldChange('dateOfManufacture', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    onClick={e => e.target.showPicker?.()} />
                                <TextField label="Length" value={form.length} size="small" disabled={isReadOnly}
                                    onChange={e => handleFieldChange('length', e.target.value)} />
                                <FormControl size="small" disabled={isReadOnly}>
                                    <InputLabel>Material</InputLabel>
                                    <Select value={form.lifelineMaterial} label="Material"
                                        onChange={e => handleFieldChange('lifelineMaterial', e.target.value)}>
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {LIFELINE_MATERIALS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField label="Owner / Company" value={form.ownerCompany} size="small" disabled={isReadOnly}
                                    onChange={e => handleFieldChange('ownerCompany', e.target.value)} />
                                <TextField label="In-Service" type="date" value={form.inServiceDate} size="small"
                                    disabled={isReadOnly} onChange={e => handleFieldChange('inServiceDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    onClick={e => e.target.showPicker?.()} />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    */}

                    {/* Job / Location & Date — hidden for now
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job / Location & Date</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Box sx={{ mb: 1 }}>
                                <ToggleButtonGroup value={jobMode} exclusive onChange={handleModeChange} disabled={isReadOnly} size="small">
                                    <ToggleButton value={MODE_JOB} sx={{ px: 2 }}>
                                        <WorkOutlineIcon sx={{ mr: 0.5 }} fontSize="small" /> Job
                                    </ToggleButton>
                                    <ToggleButton value={MODE_LOCATION} sx={{ px: 2 }}>
                                        <PlaceIcon sx={{ mr: 0.5 }} fontSize="small" /> Location
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                                {jobMode === MODE_JOB ? (
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={o => o.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                        onChange={handleJobChange}
                                        disabled={isReadOnly}
                                        renderInput={p => <TextField {...p} label="Job" size="small" />}
                                    />
                                ) : (
                                    <Autocomplete
                                        options={locationOptions}
                                        getOptionLabel={o => o.label || ''}
                                        value={locationOptions.find(l => l.label === form.jobLocation) || null}
                                        onChange={handleLocationChange}
                                        disabled={isReadOnly}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.label}>
                                                {option.label}
                                                {option.isAltJob && (
                                                    <span style={{ marginLeft: 8, fontSize: '0.7rem', backgroundColor: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>ALT</span>
                                                )}
                                            </li>
                                        )}
                                        renderInput={p => <TextField {...p} label="Location" size="small" />}
                                    />
                                )}
                                <TextField label="Inspection Date *" type="date" value={form.inspectionDate} size="small"
                                    disabled={isReadOnly} onChange={e => handleFieldChange('inspectionDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }} error={!!errors.inspectionDate}
                                    onClick={e => e.target.showPicker?.()} />
                            </Box>
                            {jobMode === MODE_JOB && form.customerName && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                                    <TextField label="Customer" value={form.customerName} size="small" InputProps={{ readOnly: true }} />
                                    {form.jobLocation && <TextField label="Location" value={form.jobLocation} size="small" InputProps={{ readOnly: true }} />}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                    */}

                    {/* PPE warning banner — visible at all times above the inspection checklist */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffb300',
                        borderLeft: '4px solid #ff8f00',
                        borderRadius: 1, p: 1, mb: 0.5, mt: 0.5,
                    }}>
                        <ShieldIcon sx={{ color: '#ff6f00', fontSize: 22, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.83rem', lineHeight: 1.4, color: '#5d4037', fontWeight: 500 }}>
                            <strong>Use Proper PPE</strong> (leather gloves &amp; safety glasses) and secure the attachment hook to a secure point before starting the inspection.
                        </Typography>
                    </Box>

                    {/* Inspection Sections — сгруппированы по INSPECTION_GROUPS:
                        1) Inspect the unit  → Connectors / Housing / Shock Pack
                        2) Verify Lifeline Operation */}
                    {INSPECTION_GROUPS.map(group => (
                        <Box key={group.id} sx={{ mt: 1, mb: 0.5 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 1, py: 0.75,
                                borderLeft: `4px solid ${group.color}`,
                                background: `linear-gradient(90deg, ${group.color}12 0%, transparent 100%)`,
                                borderRadius: '0 4px 4px 0',
                                mb: 0.5,
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: group.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {group.title}
                                </Typography>
                            </Box>
                            {group.sections.map(key => renderCheckSection(key))}
                        </Box>
                    ))}
                    {errors.checks && (
                        <Typography variant="caption" color="error" sx={{ px: 1.5 }}>{errors.checks}</Typography>
                    )}

                    {/* Notes */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Notes</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <TextField fullWidth multiline rows={3} value={form.notes} disabled={isReadOnly}
                                onChange={e => handleFieldChange('notes', e.target.value)} placeholder="Additional notes..." size="small"
                                InputProps={{
                                    endAdornment: speechSupported && !isReadOnly && (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <IconButton onClick={toggleDictation} edge="end" color={isListening ? 'error' : 'default'} size="small">
                                                {isListening ? <MicIcon /> : <MicOffIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Inspector & Signature */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Inspector & Signature</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Box sx={{ mb: 1.5 }}>
                                <TextField label="Inspector *" value={form.inspectorName} size="small" fullWidth
                                    InputProps={{ readOnly: true }}
                                    error={!!errors.inspectorName}
                                    onClick={() => !isReadOnly && setEmployeeModalOpen(true)}
                                    sx={{ cursor: isReadOnly ? 'default' : 'pointer', '& input': { cursor: isReadOnly ? 'default' : 'pointer' } }}
                                />
                            </Box>
                            <Box>
                                {form.signature ? (
                                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, textAlign: 'center' }}>
                                        <img src={form.signature} alt="Signature" style={{ maxWidth: '100%', maxHeight: 80 }} />
                                        {!isReadOnly && (
                                            <Button size="small" onClick={() => { handleFieldChange('signature', ''); setSignatureModalOpen(true); }} sx={{ mt: 0.5 }}>
                                                Re-sign
                                            </Button>
                                        )}
                                    </Box>
                                ) : (
                                    <Button variant="outlined" fullWidth onClick={() => setSignatureModalOpen(true)} disabled={isReadOnly}
                                        sx={{ py: 2, borderStyle: 'dashed', color: errors.signature ? '#f44336' : undefined, borderColor: errors.signature ? '#f44336' : undefined }}>
                                        Tap to Sign
                                    </Button>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Submit */}
                    {!isReadOnly && (
                        <Box sx={{ p: 1.5, pb: 3 }}>
                            <Button variant="contained" fullWidth size="large" onClick={handleSubmit} disabled={saving}
                                sx={{ backgroundColor: '#1565c0', py: 1.5, fontSize: '1rem', fontWeight: 600, '&:hover': { backgroundColor: '#0d47a1' } }}>
                                Submit Inspection
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SignatureModal
                allowAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                onSave={handleSignatureSave}
                existingSignature={form.signature}
                employeeName={form.inspectorName}
            />
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectEmployee}
            />
            <PdfPreviewModal
                open={pdfPreviewOpen}
                onClose={() => { setPdfPreviewOpen(false); setPdfBlob(null); }}
                pdfBlob={pdfBlob}
                filename={pdfFilename}
                loading={pdfLoading}
            />
            <QrScannerModal
                open={qrScannerOpen}
                onClose={() => setQrScannerOpen(false)}
                onDetected={handleQrDetected}
                title="Scan SRD Device QR"
                hint="Point the camera at the QR label on the device. You can also paste the URL/token manually below."
            />
            <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={saving}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default SrdInspectionForm;
